<template>
  <component
    :is="resolvedComponent"
    v-if="resolvedComponent"
    :alt="alt"
    :class="[
      size ? `icon-${size}x` : null,
      color ? `icon-fi--${color}` : null,
      internalRotation ? `icon-fi--${internalRotation}` : null,
      iconPadding24 ? 'icon-fi--padding' : null,
    ]"
    @click="$emit('click')"
  />
</template>

<script lang="ts">
import voidComponent from 'svg-icons/void.svg';
import { defineAsyncComponent } from 'vue';

const camelCase = (component: string) =>
  component.replace(/(?:^|-)(.)/g, (_match, letter: string) => letter.toUpperCase());

const components: Record<string, any> = Object.fromEntries(
  Object.entries(import.meta.glob(['svg-icons/*.svg'])).map(([key, value]) => [
    key.replace(
      /^\/src\/assets\/svg-icons\/(.*)\.svg$/,
      (_match, inKebabCase: string) => `${camelCase(inKebabCase)}Icon`,
    ),
    defineAsyncComponent({
      loader: value as () => Promise<any>,
      loadingComponent: voidComponent as any,
      delay: 0,
    }),
  ]),
);

/**
 * Pre-resolves the internal `resolvedComp` closure of each async component so that
 * Vue's hydration pass can render them synchronously rather than tearing down the
 * SSR-rendered subtree and re-mounting after a microtask.
 *
 * Call this (and await it) after chunk preloading but before app.mount().
 * Paths come from the `__ICON_PRELOAD__` JSON blob the server embeds in the HTML
 * based on ctx.modules entries collected during renderToString.
 */
export const prewarmIconComponents = (iconModulePaths: string[]): Promise<void> =>
  Promise.allSettled(
    iconModulePaths.map((path) => {
      // 'src/assets/svg-icons/globe.svg' → 'GlobeIcon'
      const name = path.slice('src/assets/svg-icons/'.length, -'.svg'.length);
      const comp = components[camelCase(`${name}-icon`)];
      return (comp as any)?.__asyncLoader?.();
    }),
  ).then(() => undefined);
</script>

<script setup lang="ts">
import { computed, shallowRef, useSSRContext, watch } from 'vue';

defineOptions({ name: 'IconFi' });

interface Props {
  icon: string | null | undefined;
  size?: string | null;
  color?: string | null;
  rotate?: string | null;
  alt?: string | null;
  iconPadding24?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: null,
  color: null,
  rotate: null,
  alt: null,
  iconPadding24: false,
});

defineEmits<{
  click: [];
}>();

const resolvedComponent = shallowRef<any>(null);

const internalRotation = computed(() => {
  let rotation: number | null = null;
  if (props.icon === 'chevron-up') {
    rotation = 180;
  } else if (props.icon === 'chevron-left') {
    rotation = 90;
  } else if (props.icon === 'chevron-right') {
    rotation = 270;
  } else if (props.icon === 'arrow-left') {
    rotation = 180;
  }
  if (!rotation) {
    return props.rotate;
  }
  if (props.rotate) {
    rotation = (rotation + Number.parseInt(props.rotate)) % 360;
  }
  return String(rotation);
});

const resolveIconFile = (icon: string): string => {
  if (icon === 'chevron-up' || icon === 'chevron-left' || icon === 'chevron-right')
    return 'chevron-down';
  if (icon === 'arrow-left') return 'arrow-right';
  return icon;
};

const setIconName = (icon: string | null) => {
  if (!icon) {
    resolvedComponent.value = null;
    return;
  }

  const i = resolveIconFile(icon);
  const camelCased = camelCase(`${i}-icon`);
  const component = components[camelCased];
  if (!component) {
    console.error('unknown icon', i);
    resolvedComponent.value = null;
    return;
  }
  resolvedComponent.value = component;
};

watch(
  () => props.icon,
  (newIcon) => {
    setIconName(newIcon ?? null);
    if (IS_SSR && newIcon) {
      const ctx = useSSRContext();
      if (ctx?.modules) {
        ctx.modules.add(`src/assets/svg-icons/${resolveIconFile(newIcon)}.svg`);
      }
    }
  },
  { immediate: true },
);

defineExpose({
  components,
});
</script>

<style src="./icon-fi" />
